import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { fetchCompaniesByUserId } from "../../db/Companies";
import SubscriptionCompanyCardCheckbox from "./BundleComponents/SubscriptionCompanyCard";
import { bundles } from "./BundlesData";
import { generateStripeLinks } from "../../db/Subscriptions";
import { Link } from "react-router-dom";
import Button from "../../components/controls/Button";

export default function SubscriptionForClient({
  selectedBundleKey,
  selectedDuration,
  onClose,
}) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [stripeLinks, setStripeLinks] = useState({});
  const [selectedReach, setSelectedReach] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [linksGenerated, setLinksGenerated] = useState(false);

  const selectedBundle = bundles[selectedBundleKey];
  const bundleLabel = selectedBundle
    ? `${t(`becomePartner.bundles.${selectedBundleKey}.name`)}`
    : null;

  const availableReaches = selectedBundle?.pricing
    ?.filter((p) => p.duration === selectedDuration)
    ?.map((p) => p.reach)
    ?.filter((v, i, a) => a.indexOf(v) === i); // remove duplicates

  const toggleCompany = (companyId) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId]
    );
  };

  const handleGeneratePayment = async () => {
    if (
      !selectedCompanyIds.length ||
      !selectedBundleKey ||
      !selectedDuration ||
      !selectedReach
    )
      return;

    const subscriptions = selectedCompanyIds.map((companyId) => ({
      companyId,
      priceId: selectedBundleKey,
      duration: selectedDuration,
      reach: selectedReach,
    }));

    try {
      const links = await generateStripeLinks({
        subscriptions,
        createdBy: currentUser.id,
      });

      const linksMap = {};
      links.forEach(({ companyId, url }) => {
        linksMap[companyId] = url;
      });

      setStripeLinks(linksMap);
      setLinksGenerated(true);
    } catch (err) {
      console.error("Erreur génération liens Stripe", err);
    }
  };

  useEffect(() => {
    if (currentUser?.id) {
      fetchCompaniesByUserId(currentUser.id).then(setCompanies);
    }
  }, [currentUser]);

  useEffect(() => {
    if (availableReaches?.length === 1) {
      setSelectedReach(availableReaches[0]);
      setCurrentStep(2);
    }
  }, [availableReaches]);

  return (
    <div className="space-y-6 text-primary">
      {bundleLabel && (
        <p className="text-sm text-gray-600">
          {t("subscriptionModal.selectedBundle", "Forfait sélectionné")}:{" "}
          <span className="font-medium text-gray-800">{bundleLabel}</span>
        </p>
      )}

      {currentStep === 1 && (
        <>
          <h3 className="text-lg font-semibold">
            {t(
              "subscriptionModal.chooseReach",
              "Choisissez la portée de votre abonnement"
            )}
          </h3>
          <div className="flex flex-wrap gap-2">
            {availableReaches?.length > 1 && (
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {availableReaches.map((reachOption) => {
                    const isSelected = selectedReach === reachOption;
                    return (
                      <div
                        key={reachOption}
                        onClick={() => setSelectedReach(reachOption)}
                        className={`cursor-pointer rounded-lg border p-4 transition-all text-center shadow-sm ${
                          isSelected
                            ? "bg-gradient-to-br from-[#FFD54A]/10 via-[#D4E66D]/10 to-[#A3C66A]/10 text-primary border-[#A3C66A] scale-[1.1]"
                            : "bg-white text-gray-800 hover:border-[#A3C66A]"
                        }`}
                      >
                        <p className="text-lg font-semibold capitalize">
                          {t(`subscriptionModal.${reachOption}`)}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {reachOption === "local" &&
                            t(
                              "subscriptionModal.reachLocalDescription",
                              "Promotion dans votre ville"
                            )}
                          {reachOption === "regional" &&
                            t(
                              "subscriptionModal.reachRegionalDescription",
                              "Visibilité dans votre région"
                            )}
                          {reachOption === "provincial" &&
                            t(
                              "subscriptionModal.reachProvincialDescription",
                              "Portée à l’échelle du Québec"
                            )}
                        </p>

                        {/* 👉 Prix affiché ici */}
                        <p className="text-sm text-primary font-medium mt-2">
                          {(() => {
                            const price = selectedBundle.pricing.find(
                              (p) =>
                                p.reach === reachOption &&
                                p.duration === selectedDuration
                            );
                            return price
                              ? `${price.finalPrice.toFixed(2)} $`
                              : "";
                          })()}
                          <p className="text-sm italic">{`Paiement aux ${selectedDuration} mois`}</p>
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="text-right mt-6">
            <Button
              onClick={() => setCurrentStep(2)}
              color="primary"
              size="md"
              disabled={!selectedReach}
              className="px-4 py-2 mb-4"
            >
              {t("next", "Suivant")}
            </Button>
          </div>
        </>
      )}

      {currentStep === 2 && (
        <>
          <h3 className="text-lg font-semibold">
            {companies.length === 0 ? (
              <div className="space-y-2">
                <p>{t("Vous n'avez aucune compagnie d'enregistrée")}</p>
                <Link
                  to="/manage-company"
                  className="inline-block py-2 px-4 rounded-full bg-papayaTitle text-white hover:bg-papayaTitle/80 transition"
                >
                  {t("Inscrivez-vous ici")}
                </Link>
              </div>
            ) : (
              t(
                "subscriptionModal.chooseCompanies",
                "Choisissez les compagnies à abonner"
              )
            )}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            {companies.map((company) => (
              <SubscriptionCompanyCardCheckbox
                key={company.id}
                company={company}
                isSelected={selectedCompanyIds.includes(company.id)}
                onToggle={toggleCompany}
                paymentUrl={stripeLinks[company.id]}
              />
            ))}
          </div>

          <div className="text-right mt-4 space-x-2">
            <Button
              onClick={linksGenerated ? onClose : handleGeneratePayment}
              color="primary"
              size="md"
              disabled={
                !linksGenerated &&
                (!selectedCompanyIds.length ||
                  !selectedBundleKey ||
                  !selectedDuration ||
                  !selectedReach)
              }
              className="px-4 py-2" // optionnel, ton Button a déjà le padding via `size`
            >
              {linksGenerated
                ? t("subscriptionModal.finish", "Terminer")
                : t(
                    "subscriptionModal.confirmSubscription",
                    "Générer les liens d’abonnement"
                  )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
